import { Box, Theme, makeStyles } from '@material-ui/core';
import { WbTruncatedTypographyWrapper } from '../WbTruncatedTypographyWrapper';
import clsx from 'clsx';
import { useBreakpointValue } from '../../hooks';
import { FC } from 'react';
import { startCase } from 'lodash';

const useStyles = makeStyles<
  Theme,
  { direction: 'column' | 'row'; depth: number }
>(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  nestedRoot: {
    paddingTop: theme.spacing(1),
  },
  row: prop => ({
    display: 'flex',
    flexDirection: prop.direction,
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  }),
  key: {
    width: 250,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textTransform: 'capitalize',
  },
  nestedKey: prop => ({
    width: 250,
    fontWeight: 500,
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(prop.depth * 4),
    textTransform: 'capitalize',
  }),
  value: prop => ({
    color: theme.palette.text.secondary,
    flex: 1,
    marginLeft: prop.direction === 'column' ? theme.spacing(prop.depth * 4) : 0,
  }),
}));

type Props = {
  metadata: Record<string, any>;
  depth?: number;
  customClass?: String;
};

export const WbMetadataTable: FC<Props> = ({
  metadata,
  depth = 0,
  customClass,
}) => {
  const direction: 'column' | 'row' =
    useBreakpointValue({ xs: 'column', sm: 'row' }) ?? 'row';

  const classes = useStyles({ direction, depth });

  if (!metadata || typeof metadata !== 'object') {
    return null;
  }

  return (
    <div className={clsx(classes.root, customClass)}>
      {Object.entries(metadata).map(([key, value]) => {
        const isNestedObject =
          typeof value === 'object' && value !== null && !Array.isArray(value);

        const isArrayOfObjects =
          Array.isArray(value) &&
          value.some(v => typeof v === 'object' && v !== null);

        const isArrayOfPrimitives =
          Array.isArray(value) && value.every(v => typeof v !== 'object');

        const keyStyle = depth > 0 ? classes.nestedKey : classes.key;

        return (
          <Box key={key}>
            <Box className={classes.row}>
              <WbTruncatedTypographyWrapper
                customClass={keyStyle}
                value={startCase(key)}
                enableTooltip={false}
                maxLines={5}
              />

              {/* Primitive value */}
              {!isNestedObject && !Array.isArray(value) && (
                <WbTruncatedTypographyWrapper
                  customClass={classes.value}
                  value={value ? String(value) : '-'}
                  enableTooltip={false}
                  maxLines={5}
                />
              )}

              {/* Array of primitives */}
              {isArrayOfPrimitives && (
                <WbTruncatedTypographyWrapper
                  customClass={classes.value}
                  value={value.length > 0 ? value.join(', ') : '-'}
                  enableTooltip={false}
                  maxLines={5}
                />
              )}
            </Box>

            {/* Nested object */}
            {isNestedObject && (
              <WbMetadataTable
                metadata={value}
                depth={depth + 1}
                customClass={classes.nestedRoot}
              />
            )}

            {/* Array of objects */}
            {isArrayOfObjects &&
              value.map((item, index) => (
                <WbMetadataTable
                  key={`${key}-${index}`}
                  metadata={item}
                  depth={depth + 1}
                  customClass={classes.nestedRoot}
                />
              ))}
          </Box>
        );
      })}
    </div>
  );
};
